import { Affine, Vec2 } from './math';
import type { Entity } from './types';

/** Returns a new entity of the same type with all defining points mapped through `m`. */
export function transformEntity(entity: Entity, m: Affine): Entity {
  const scale = m.scaleFactor;
  const rotation = m.rotationAngle;

  switch (entity.type) {
    case 'line':
      return { ...entity, a: m.apply(entity.a), b: m.apply(entity.b) };
    case 'polyline':
      return { ...entity, vertices: entity.vertices.map((v) => m.apply(v)) };
    case 'circle':
      return { ...entity, center: m.apply(entity.center), radius: entity.radius * scale };
    case 'arc':
      return {
        ...entity,
        center: m.apply(entity.center),
        radius: entity.radius * scale,
        startAngle: entity.startAngle + rotation,
        endAngle: entity.endAngle + rotation,
      };
    case 'ellipse':
      return {
        ...entity,
        center: m.apply(entity.center),
        rx: entity.rx * scale,
        ry: entity.ry * scale,
        rotation: entity.rotation + rotation,
      };
    case 'spline':
      return { ...entity, controlPoints: entity.controlPoints.map((v) => m.apply(v)) };
    case 'text':
      return {
        ...entity,
        position: m.apply(entity.position),
        rotation: entity.rotation + rotation,
        height: entity.height * scale,
      };
    case 'hatch':
      return {
        ...entity,
        loops: entity.loops.map((loop) => loop.map((p) => m.apply(p))),
        origin: m.apply(entity.origin),
      };
    case 'dimension':
      return {
        ...entity,
        p1: m.apply(entity.p1),
        p2: m.apply(entity.p2),
        offset: entity.offset * scale,
        textHeight: entity.textHeight * scale,
        arrowSize: entity.arrowSize * scale,
      };
    default:
      return entity;
  }
}

export function translateEntity(entity: Entity, delta: Vec2): Entity {
  return transformEntity(entity, Affine.translation(delta.x, delta.y));
}

export function rotateEntity(entity: Entity, center: Vec2, angle: number): Entity {
  return transformEntity(entity, Affine.rotation(angle, center));
}

export function scaleEntity(entity: Entity, center: Vec2, factor: number): Entity {
  return transformEntity(entity, Affine.scaling(factor, factor, center));
}

/** Axis-aligned mirror about a horizontal or vertical line through `origin`. */
export function mirrorEntity(entity: Entity, origin: Vec2, horizontal: boolean): Entity {
  const m = horizontal
    ? new Affine(1, 0, 0, -1, 0, 2 * origin.y)
    : new Affine(-1, 0, 0, 1, 2 * origin.x, 0);
  return transformEntity(entity, m);
}
